/**
 * "Whose cost?" — the control that made this screen worth rebuilding.
 *
 * The old Calculator asked a manager to type in facts the league has written down: how the player
 * was acquired, what round value he carries, how many keeper years he has served, whether he was
 * ever dropped. Four chances to get it wrong, on a screen whose entire purpose is to be right —
 * and the app knew all four the whole time.
 *
 * So the picker leads and fills the form. Manual entry is one tap below and unchanged in power:
 * a hypothetical is a real use ("what would a 14th-round flyer cost me next year?") and it is a
 * different use from pricing a man on a roster.
 *
 * OTHER MANAGERS' ROSTERS ARE HERE, one tap in. Working out what a rival's keeper costs him is a
 * real question in August — it is how you guess which of his players will be in the draft — but
 * it is not the question this screen is opened for, so it is not the default.
 */

import { useMemo, useState } from 'react';
import { currentManagers, gmOfTeam, managerShort, teamOfGm } from '../../domain/identity.js';
import { splitName } from '../../domain/keeperRow.js';
import { type CalcSource, displayRound } from '../../domain/calculator.js';
import { LEAGUE_2026 } from '../../domain/config.js';
import { PlayerClubChip, PositionChip } from '../chips.js';
import { useScrollLock } from '../useScrollLock.js';
import { dock } from '../dock.js';

export interface PickerProps {
  /** Every rostered player in the league, priced or not. */
  roster: readonly CalcSource[];
  /** The manager whose roster opens first. */
  gm: string;
  /** The player currently in the form, or null when the form is a hypothetical. */
  selected: CalcSource | null;
  onPick: (row: CalcSource) => void;
  onManual: () => void;
  /** True from 900px up: the manual button sits beside the picker instead of under it. */
  wide: boolean;
}

/** `NACUA,` then `Puka` — the same name line the declaration row draws. */
function NameLine({ player, big }: { player: string; big?: boolean }) {
  const { surname, given } = splitName(player);
  return (
    <span className={`kcname${big ? ' big' : ''}`}>
      <b>{surname}{given ? ',' : ''}</b>
      {given && <span>{given}</span>}
    </span>
  );
}

export function PlayerPicker(props: PickerProps) {
  const { roster, gm, selected, onPick, onManual, wide } = props;
  const [open, setOpen] = useState(false);
  const mine = useMemo(() => teamOfGm(gm), [gm]);
  const [team, setTeam] = useState(mine);

  const others = useMemo(
    () => currentManagers().map((m) => m.id).filter((id) => teamOfGm(id) !== mine),
    [mine],
  );

  const list = useMemo(
    /*
      Ordered by what he COSTS, dearest first — the round the Grid reads rather than the round
      stored on the row, or the waiver pickups (stored as 0) sort to the top as the cheapest
      names in the league pretending to be the most expensive.
    */
    () => roster.filter((r) => r.team === team)
      .map((r) => ({ row: r, rd: displayRound(r, LEAGUE_2026) }))
      .sort((a, b) => a.rd - b.rd || a.row.player.localeCompare(b.row.player)),
    [roster, team],
  );

  useScrollLock(open);

  /** `your roster · Rd 12`, or whose roster it is when it is not yours. */
  const provenance = selected
    ? `${selected.team === mine ? 'your roster' : managerShort(gmOfTeam(selected.team))} · Rd ${displayRound(selected, LEAGUE_2026)}`
    : 'a hypothetical';

  return (
    <>
      <div className="kcpickrow">
        <button type="button" className="kcpicked" onClick={() => { setTeam(mine); setOpen(true); }}
          aria-haspopup="dialog" aria-expanded={open}>
          <span className="kcpickedbody">
            {selected
              ? <NameLine player={selected.player} />
              : <span className="kcname"><b>Choose a player</b></span>}
            <span className="kcmeta">
              {selected && <PlayerClubChip player={selected.player} />}
              {selected && <PositionChip pos={selected.pos} />}
              <span className="mut">{provenance}</span>
            </span>
          </span>
          <i className="kccaret" aria-hidden="true" />
        </button>
        {wide && (
          <button type="button" className="kcmanual wide" onClick={onManual}>Enter manually</button>
        )}
      </div>

      {!wide && (
        <>
          <div className="kcor"><i /><span>or</span><i /></div>
          <button type="button" className="kcmanual" onClick={onManual}>
            Enter a player manually
          </button>
        </>
      )}

      {/* Docked — `position: fixed` inside a transformed `main`. See `src/ui/dock.tsx`. */}
      {open && dock(
        <>
          <button type="button" className="sheetscrim kcscrim" aria-label="Close"
            onClick={() => setOpen(false)} />
          <aside className="kcsheet" role="dialog" aria-modal="true" aria-label="Choose a player">
            <header>
              <h2>Whose cost?</h2>
              <button type="button" className="kcsheetclose" aria-label="Close"
                onClick={() => setOpen(false)}>&times;</button>
            </header>

            {/*
              YOUR ROSTER IS A TAB, not a filter you have to find. The nine others are behind it
              in the same strip, so switching to a rival's team is one tap and arriving at one is
              never an accident.
            */}
            <div className="kcteams" role="tablist" aria-label="Whose roster">
              <button type="button" role="tab" aria-selected={team === mine}
                className={team === mine ? 'on' : ''} onClick={() => setTeam(mine)}>
                Your roster
              </button>
              {others.map((id) => (
                <button key={id} type="button" role="tab" aria-selected={team === teamOfGm(id)}
                  className={team === teamOfGm(id) ? 'on' : ''}
                  onClick={() => setTeam(teamOfGm(id))}>
                  {managerShort(id)}
                </button>
              ))}
            </div>

            <ul className="kcroster">
              {list.map(({ row: r, rd }) => (
                <li key={r.player}>
                  <button type="button" className={r.player === selected?.player ? 'on' : ''}
                    onClick={() => { onPick(r); setOpen(false); }}>
                    <NameLine player={r.player} big />
                    <span className="kcrchips">
                      <PlayerClubChip player={r.player} />
                      <PositionChip pos={r.pos} />
                    </span>
                    <span className="kcrval">Rd {rd}</span>
                  </button>
                </li>
              ))}
              {list.length === 0 && <li className="kcempty">No rostered players on record.</li>}
            </ul>
          </aside>
        </>
      )}
    </>
  );
}
